import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { submitAccountDeletionRequest } from '../services/firebase';
import { buildWhatsappUrl, openWhatsappChat } from '../utils/support';
import { useLanguage } from '../i18n/LanguageContext';
import useScrollLock from '../utils/useScrollLock';

const REASON_OPTIONS = [
  { id: 'not_using', tk: 'del.reasonNotUsing' },
  { id: 'privacy_concerns', tk: 'del.reasonPrivacy' },
  { id: 'switched_service', tk: 'del.reasonSwitched' },
  { id: 'creating_new', tk: 'del.reasonNewAccount' },
  { id: 'other', tk: 'del.reasonOther' },
];

export default function DeleteAccountModal({ open, onClose, userProfile, user }) {
  const { t } = useLanguage();
  const [phone, setPhone] = useState(userProfile?.phone || user?.phoneNumber || '');
  const [reason, setReason] = useState('not_using');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  useScrollLock(open);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanPhone = String(phone || '').replace(/\D/g, '').slice(-10);
    if (cleanPhone.length !== 10) {
      toast.error(t('del.phoneInvalid'));
      return;
    }
    setSubmitting(true);
    try {
      await submitAccountDeletionRequest({
        phone: cleanPhone,
        reason,
        details,
        role: 'passenger',
      });
      setSubmitted(true);
      toast.success(t('del.submitted'));
    } catch (err) {
      console.error('Delete account request error:', err);
      toast.error(t('del.submitFail'));
    } finally {
      setSubmitting(false);
    }
  };

  const cleanPhone = String(phone || '').replace(/\D/g, '').slice(-10);
  const selectedReasonLabel = t(REASON_OPTIONS.find((r) => r.id === reason)?.tk || 'del.reasonOther');
  const finalReason = details?.trim()
    ? `${selectedReasonLabel}: ${details.trim()}`
    : selectedReasonLabel;
  const userName = userProfile?.name?.trim() || user?.displayName?.trim() || t('del.valuedUser');
  const formattedDate = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const formattedTime = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });

  const waMessage = [
    'Hi MyTownly Team,',
    '',
    'I have submitted an Account & Data Deletion Request under DPDP Act 2023.',
    '',
    '📋 Request Details:',
    `• Name: ${userName}`,
    `• Phone: +91 ${cleanPhone}`,
    `• Reason: ${finalReason}`,
    `• Submitted On: ${formattedDate}, ${formattedTime}`,
    '',
    'Please review and process my erasure request. Thank you!',
  ].join('\n');

  const waUrl = buildWhatsappUrl(waMessage);

  return (
    <div className="modal-backdrop support-modal-backdrop" onClick={onClose}>
      <div
        className="support-modal-sheet"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '24px 24px 0 0',
          background: '#f8fafc',
          padding: 0,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 20px 16px',
          background: '#ffffff',
          borderBottom: '1px solid #f1f5f9',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px' }}>
                🗑️
              </div>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>{t('del.title')}</h2>
            </div>
            <p style={{ margin: '3px 0 0', fontSize: '12px', color: '#64748b' }}>
              {t('del.subtitle')}
            </p>
          </div>
          <button
            type="button"
            className="support-modal-close"
            onClick={onClose}
            style={{ position: 'static', margin: 0 }}
            aria-label={t('common.close')}
          >
            ✕
          </button>
        </div>

        {/* Scrollable Body */}
        <div style={{
          padding: '18px 18px 40px',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}>
          {submitted ? (
            <div style={{ textAlign: 'center', padding: '20px 10px', background: '#ffffff', borderRadius: '18px', border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '44px', marginBottom: '12px' }}>✅</div>
              <h3 style={{ margin: '0 0 6px', fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>{t('del.received')}</h3>
              <p style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.5, marginBottom: '20px', maxWidth: '300px', margin: '0 auto 20px' }}>
                {t('del.receivedBody', { phone: phone.replace(/\D/g, '').slice(-10) })}
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <a
                  href={waUrl}
                  onClick={(e) => {
                    e.preventDefault();
                    openWhatsappChat(waMessage);
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '12px',
                    borderRadius: '12px',
                    background: '#f0fdf4',
                    color: '#166534',
                    border: '1px solid #bbf7d0',
                    fontWeight: 700,
                    fontSize: '13px',
                    textDecoration: 'none',
                  }}
                >
                  💬 {t('del.trackWhatsapp')}
                </a>
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    padding: '12px',
                    borderRadius: '12px',
                    background: '#0f172a',
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: '14px',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Critical Notice Card */}
              <div style={{
                background: '#fef2f2',
                borderRadius: '16px',
                padding: '14px 16px',
                border: '1px solid #fecaca',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
              }}>
                <span style={{ fontSize: '18px' }}>⚠️</span>
                <div style={{ fontSize: '12px', color: '#991b1b', lineHeight: 1.45 }}>
                  <strong>{t('del.irreversibleLabel')}</strong> {t('del.irreversibleBody')}
                </div>
              </div>

              {/* Form Container Card */}
              <div style={{ background: '#ffffff', borderRadius: '18px', padding: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', letterSpacing: '0.6px', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                    {t('del.phoneLabel')}
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
                    <span style={{ padding: '12px 14px', fontSize: '14px', fontWeight: 700, color: '#475569', background: '#f1f5f9', borderRight: '1px solid #e2e8f0' }}>
                      +91
                    </span>
                    <input
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      placeholder={t('del.phonePh')}
                      value={phone.replace('+91', '').trim()}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      style={{
                        flex: 1,
                        padding: '12px 14px',
                        fontSize: '14px',
                        border: 'none',
                        background: 'transparent',
                        outline: 'none',
                        fontWeight: 600,
                        color: '#0f172a',
                      }}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', letterSpacing: '0.6px', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                    {t('del.reasonLabel')}
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '6px' }}>
                    {REASON_OPTIONS.map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setReason(opt.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '10px 14px',
                          borderRadius: '10px',
                          fontSize: '13px',
                          fontWeight: reason === opt.id ? 700 : 500,
                          color: reason === opt.id ? '#991b1b' : '#334155',
                          background: reason === opt.id ? '#fef2f2' : '#f8fafc',
                          border: reason === opt.id ? '1px solid #fca5a5' : '1px solid #e2e8f0',
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                      >
                        <span>{t(opt.tk)}</span>
                        {reason === opt.id && <span style={{ color: '#ef4444' }}>✓</span>}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', letterSpacing: '0.6px', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                    {t('del.feedbackLabel')}
                  </label>
                  <textarea
                    rows={2}
                    placeholder={t('del.feedbackPh')}
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      background: '#f8fafc',
                      fontSize: '13px',
                      resize: 'none',
                      outline: 'none',
                      fontFamily: 'inherit',
                    }}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    width: '100%',
                    padding: '14px',
                    borderRadius: '14px',
                    background: '#dc2626',
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: '14px',
                    border: 'none',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    boxShadow: '0 4px 14px rgba(220, 38, 38, 0.28)',
                  }}
                >
                  {submitting ? t('del.submitting') : t('del.confirm')}
                </button>

                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '14px',
                    background: '#ffffff',
                    color: '#475569',
                    fontWeight: 600,
                    fontSize: '14px',
                    border: '1px solid #e2e8f0',
                    cursor: 'pointer',
                  }}
                >
                  {t('del.keep')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
